import React, { useState, useEffect } from 'react';
import { Scan, Loader } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { AutoAnalysisService } from '../../../services/AutoAnalysisService';

interface QuickScanButtonProps {
  siteUrl: string;
  prospectId?: string;
  onScanComplete?: () => void;
}

/**
 * QuickScanButton - Bouton permettant de déclencher simultanément toutes les analyses disponibles
 */
const QuickScanButton: React.FC<QuickScanButtonProps> = ({ siteUrl, prospectId, onScanComplete }) => {
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  
  // Simuler la progression pendant l'analyse
  useEffect(() => {
    if (!isScanning) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev; // Ne pas aller au-delà de 90% avant la fin réelle
        return prev + 10;
      });
    }, 3000); // Incrémenter toutes les 3 secondes

    return () => clearInterval(progressInterval);
  }, [isScanning]);

  // Fonction principale pour lancer toutes les analyses
  const handleQuickScan = async () => {
    if (isScanning || !siteUrl || !prospectId) return;

    setIsScanning(true);
    setProgress(0);

    try {
      console.log('🚀 [QuickScan] Démarrage des analyses pour:', { prospectId, siteUrl });

      // Récupérer les métadonnées du prospect pour avoir company et location
      const { data: prospectData } = await supabase
        .from('prospects')
        .select('metadata, nom')
        .eq('id', prospectId)
        .single();

      const companyName = prospectData?.metadata?.company || prospectData?.nom || '';
      const location = prospectData?.metadata?.location || '';

      console.log('📋 [QuickScan] Infos prospect:', { companyName, location });

      // Utiliser AutoAnalysisService qui gère TOUTES les analyses
      // (PageSpeed, CMS, Keywords, Connectivity, Google Reviews, Score, IA Intro, IA Recommendations, Citations)
      const success = await AutoAnalysisService.runAllAnalyses(
        prospectId,
        siteUrl,
        companyName,
        location
      );

      // Progression à 100%
      setProgress(100);
      console.log('✅ [QuickScan] Analyses terminées:', success ? 'Succès' : 'Erreur partielle');

      // Petit délai pour montrer la complétion
      setTimeout(() => {
        setIsScanning(false);

        // Callback pour rafraîchir la page
        if (onScanComplete) {
          onScanComplete();
        }
      }, 500);

    } catch (error) {
      console.error('❌ [QuickScan] Erreur globale:', error);
      setIsScanning(false);
      setProgress(0);

      // Optionnel : afficher un message d'erreur à l'utilisateur
      alert('Une erreur est survenue lors de l\'analyse. Veuillez réessayer.');
    }
  };

  return (
    <button
      onClick={handleQuickScan}
      disabled={isScanning || !siteUrl}
      className={`
        relative flex items-center px-4 py-2 rounded-md shadow-sm transition-all
        ${isScanning 
          ? 'bg-blue-100 text-blue-800 cursor-not-allowed' 
          : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
        }
      `}
      title="Lancer toutes les analyses disponibles"
    >
      {isScanning ? (
        <>
          <Loader className="w-4 h-4 mr-2 animate-spin" />
          <span>Analyse en cours ({progress}%)</span>
          {/* Barre de progression */}
          <div className="absolute bottom-0 left-0 h-1 bg-blue-500 transition-all duration-300" style={{ width: `${progress}%` }}></div>
        </>
      ) : (
        <>
          <Scan className="w-4 h-4 mr-2" />
          <span>Quick Scan</span>
        </>
      )}
    </button>
  );
};

export default QuickScanButton;
